import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  HomeIcon,
  UsersIcon,
  CalendarIcon,
  PencilSquareIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { getClients } from '../../services/clientService';
import useCommandStore from '../../store/commandStore';

const routes = [
  { label: 'Dashboard',    to: '/',            icon: HomeIcon },
  { label: 'Clients',      to: '/clients',     icon: UsersIcon },
  { label: 'Appointments', to: '/appointments', icon: CalendarIcon },
  { label: 'Notes',        to: '/notes',       icon: PencilSquareIcon },
  { label: 'Tasks',        to: '/tasks',       icon: ClipboardDocumentCheckIcon },
  { label: 'Messages',     to: '/messages',    icon: ChatBubbleLeftRightIcon },
  { label: 'Billing',      to: '/billing',     icon: CreditCardIcon },
  { label: 'Insurance',    to: '/insurance',   icon: ShieldCheckIcon },
  { label: 'Reports',      to: '/reports',     icon: ChartBarIcon },
  { label: 'Intake Forms', to: '/intake-forms', icon: ClipboardDocumentListIcon },
  { label: 'Settings',     to: '/settings',    icon: CogIcon },
];

function getInitials(c) {
  return `${c.firstName?.[0] ?? ''}${c.lastName?.[0] ?? ''}`.toUpperCase();
}

export default function CommandPalette() {
  const { open, setOpen } = useCommandStore();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { search: query, limit: 5 }],
    queryFn: () => getClients({ search: query, limit: 5 }),
    enabled: query.length >= 2,
    staleTime: 10_000,
  });

  const clients = useMemo(
    () => clientsData?.clients ?? clientsData ?? [],
    [clientsData]
  );

  const filteredRoutes = query.length < 2
    ? routes
    : routes.filter((r) => r.label.toLowerCase().includes(query.toLowerCase()));

  const handleSelect = useCallback((value) => {
    if (!value) return;
    setOpen(false);
    setQuery('');
    navigate(value);
  }, [navigate, setOpen]);

  // Reset query on close
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <Transition show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog onClose={() => setOpen(false)} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 pt-[15vh]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95 -translate-y-2"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 -translate-y-2"
          >
            <Dialog.Panel className="mx-auto max-w-xl w-full bg-white dark:bg-neutral-800 rounded-xl shadow-modal border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <Combobox onChange={handleSelect}>
                {/* Search input */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
                  <MagnifyingGlassIcon className="w-5 h-5 text-neutral-400 shrink-0" />
                  <Combobox.Input
                    autoFocus
                    className="flex-1 bg-transparent text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none"
                    placeholder="Search pages, clients, actions…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <kbd className="hidden sm:block text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-500 px-1.5 py-0.5 rounded font-mono">
                    ESC
                  </kbd>
                </div>

                <Combobox.Options static className="max-h-80 overflow-y-auto py-2">
                  {/* Client results */}
                  {query.length >= 2 && clients.length > 0 && (
                    <div>
                      <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-400">
                        Clients
                      </p>
                      {clients.map((client) => (
                        <Combobox.Option
                          key={client.id}
                          value={`/clients/${client.id}`}
                          className={({ active }) =>
                            `flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors
                             ${active ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`
                          }
                        >
                          <div className="w-7 h-7 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-600 dark:text-neutral-300 shrink-0">
                            {getInitials(client)}
                          </div>
                          <span className="flex-1 text-sm text-neutral-900 dark:text-neutral-100">
                            {client.firstName} {client.lastName}
                          </span>
                          <ArrowRightIcon className="w-3.5 h-3.5 text-neutral-400" />
                        </Combobox.Option>
                      ))}
                    </div>
                  )}

                  {/* Page navigation */}
                  {filteredRoutes.length > 0 && (
                    <div>
                      <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-400">
                        {query.length >= 2 ? 'Pages' : 'Navigate'}
                      </p>
                      {filteredRoutes.map((route) => (
                        <Combobox.Option
                          key={route.to}
                          value={route.to}
                          className={({ active }) =>
                            `flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors
                             ${active ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`
                          }
                        >
                          <div className="w-7 h-7 rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center shrink-0">
                            <route.icon className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                          </div>
                          <span className="flex-1 text-sm text-neutral-900 dark:text-neutral-100">
                            {route.label}
                          </span>
                          <ArrowRightIcon className="w-3.5 h-3.5 text-neutral-400" />
                        </Combobox.Option>
                      ))}
                    </div>
                  )}

                  {query.length >= 2 && filteredRoutes.length === 0 && clients.length === 0 && (
                    <p className="px-4 py-8 text-center text-sm text-neutral-500">
                      No results for &ldquo;{query}&rdquo;
                    </p>
                  )}
                </Combobox.Options>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
